import { useState, useEffect } from "react"
import Modal from "../Modal"
import Button from "../Button"
import api from "../../services/api"

const OrganizationFormModal = ({ isOpen, onClose, onSuccess, organization = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: ''
  })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const isEditing = !!organization

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        slug: organization.slug || ''
      })
    } else {
      setFormData({
        name: '',
        slug: ''
      })
    }
    setErrors({})
  }, [organization, isOpen])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (formData.slug && !/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setSaving(true)

    try {
      if (isEditing) {
        await api.updateOrganization(organization.id, formData)
      } else {
        await api.createOrganization(formData)
      }
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to save organization:', error)
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors)
      } else {
        setErrors({
          submit: error.response?.data?.message || 'Failed to save organization'
        })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose} width="md:max-w-screen-md">
      <div>
        <h2 className="mb-4 text-2xl font-bold text-gray-900">
          {isEditing ? "Edit Organization" : "Create Organization"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="mb-1 block text-sm font-medium text-gray-700">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-brand ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="e.g., Hope Center"
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
            </div>

            {/* Slug Field */}
            <div>
              <label htmlFor="slug" className="mb-1 block text-sm font-medium text-gray-700">
                Slug
                <span className="ml-2 text-xs text-gray-500">
                  (optional, auto-generated if empty)
                </span>
              </label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-brand ${
                  errors.slug ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="e.g., hope-center"
              />
              {errors.slug && <p className="mt-1 text-sm text-red-500">{errors.slug}</p>}
              <p className="mt-1 text-xs text-gray-500">
                Used in URLs. Lowercase letters, numbers, and hyphens only.
              </p>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="secondary-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={saving}>
              {saving ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className={`fa ${isEditing ? "fa-save" : "fa-plus"} mr-2`}></i>
                  {isEditing ? "Save Changes" : "Create Organization"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default OrganizationFormModal
